// src/components/admin/PendingDecisions.jsx
// Editor's "Submitted Reviews" page - lists manuscripts awaiting the editor's
// decision (invited after reviewers submitted their reports).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  FileText,
  Eye,
  Download,
  RefreshCw,
  Loader,
  Inbox,
  Calendar,
  Search,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import { showToast } from '../../utils/toast';
import { formatDate, formatStatus, getStatusClass } from '../../utils/utils.global';

const PendingDecisions = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [decisions, setDecisions] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [exporting, setExporting] = useState({});
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [exportingSelected, setExportingSelected] = useState(null);

  const fetchPendingDecisions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/editors/backend/editors/pending-decisions');
      if (response.data.status === 'success') {
        setDecisions(response.data.data);
      } else {
        showToast(response.data.message || 'Failed to load pending decisions', 'error');
      }
    } catch (error) {
      console.error('Error fetching pending decisions:', error);
      showToast('Failed to load pending decisions. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingDecisions();
  }, [fetchPendingDecisions]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Client-side search and filters
  const filteredDecisions = useMemo(() => {
    const query = (debouncedSearchTerm || '').trim().toLowerCase();

    return decisions.filter((decision) => {
      if (query) {
        const searchable = [decision.title, decision.revision_id, decision.article_type]
          .filter(Boolean).join(' ').toLowerCase();
        if (!searchable.includes(query)) return false;
      }

      const status = (decision.invitation_status || decision.status || '').toLowerCase();
      if (filterStatus !== 'all' && status !== filterStatus) return false;

      if (filterType !== 'all' && decision.article_type !== filterType) return false;

      return true;
    });
  }, [decisions, debouncedSearchTerm, filterStatus, filterType]);

  const articleTypes = useMemo(
    () => [...new Set(decisions.map(d => d.article_type).filter(Boolean))],
    [decisions]
  );

  const itemsPerPage = 10;
  const totalItems = filteredDecisions.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const displayDecisions = filteredDecisions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilterStatus('all');
    setFilterType('all');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };



  const handleReview = (decision) => {
    navigate(`/editors/decision/${decision.revision_id}`);
  };

  const handleExportReviews = async (decision, format) => {
    const articleId = decision.revision_id;
    setExporting((prev) => ({ ...prev, [articleId]: format }));
    try {
      const response = await axios.post('/editors/export-all-reviews', { articleId, format }, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reviews_${articleId}.${format === 'docx' ? 'docx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast(`Reviewer reports exported as ${format.toUpperCase()}`, 'success');
    } catch (error) {
      console.error('Error exporting reviewer reports:', error);
      showToast('Failed to export reviewer reports. Please try again.', 'error');
    } finally {
      setExporting((prev) => ({ ...prev, [articleId]: null }));
    }
  };

  const toggleSelectArticle = (revisionId) => {
    setSelectedArticles((prev) =>
      prev.includes(revisionId)
        ? prev.filter((id) => id !== revisionId)
        : [...prev, revisionId]
    );
  };

  const toggleSelectAll = () => {
    const all = filteredDecisions.map((d) => d.revision_id);
    const allSelected = all.length > 0 && all.every((id) => selectedArticles.includes(id));
    setSelectedArticles((prev) =>
      allSelected
        ? prev.filter((id) => !all.includes(id))
        : [...new Set([...prev, ...all])]
    );
  };

  const clearSelection = () => setSelectedArticles([]);

  const handleExportSelected = async (format) => {
    if (selectedArticles.length === 0) return;
    setExportingSelected(format);
    try {
      const response = await axios.post(
        '/editors/export-all-reviews',
        { articleIds: selectedArticles, format },
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `selected_reviews.${format === 'docx' ? 'docx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast(`Selected reviewer reports exported as ${format.toUpperCase()}`, 'success');
    } catch (error) {
      console.error('Error exporting selected reviews:', error);
      showToast('Failed to export selected reviews. Please try again.', 'error');
    } finally {
      setExportingSelected(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading submitted reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <ClipboardList className="w-6 h-6 mr-3 text-[--asfi-purple]" />
            Submitted Reviews
          </h1>
          <p className="text-gray-600 mt-1">
            Manuscripts awaiting your editorial decision. Review the reports, compile the decision
            letter and notify the corresponding author.
          </p>
        </div>
        <button
          onClick={fetchPendingDecisions}
          className="p-2 text-gray-500 hover:text-[--asfi-purple] hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Search and Filters */}
      {decisions.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by title, ID, or article type..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="invite_sent">Invite Sent</option>
                <option value="under_editor_decision">Under Editor Decision</option>
              </select>
            </div>

            {/* Article Type Filter */}
            <div>
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                {articleTypes.map((type, index) => (
                  <option key={index} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Results Info */}
          <div className="mt-4 flex items-center justify-between text-sm">
            <p className="text-gray-600">
              Showing <span className="font-medium">{totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
              <span className="font-medium">{totalItems}</span> manuscripts
            </p>
            {(searchTerm || filterStatus !== 'all' || filterType !== 'all') && (
              <button
                onClick={clearFilters}
                className="text-purple-600 hover:text-purple-800 text-sm font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {/* Selected Export Toolbar */}
      {selectedArticles.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <p className="text-sm font-medium text-purple-800">
              <span className="font-bold">{selectedArticles.length}</span> manuscript{selectedArticles.length > 1 ? 's' : ''} selected
            </p>
            <button
              onClick={clearSelection}
              className="text-purple-600 hover:text-purple-800 text-sm font-medium underline"
            >
              Clear selection
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-purple-800 font-medium mr-1">Export selected reports:</span>
            <button
              onClick={() => handleExportSelected('pdf')}
              disabled={!!exportingSelected}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportingSelected === 'pdf' ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              PDF
            </button>
            <button
              onClick={() => handleExportSelected('docx')}
              disabled={!!exportingSelected}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportingSelected === 'docx' ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Word
            </button>
          </div>
        </div>
      )}

      {decisions.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">No manuscripts awaiting decision</h3>
          <p className="text-gray-500">
            When reviewers submit their reports, you will be notified and the manuscript will appear here.
          </p>
        </div>
      ) : filteredDecisions.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">No matching manuscripts</h3>
          <p className="text-gray-500">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-purple-600">
                <tr>
                  <th className="px-4 py-4 w-10">
                    <input
                      type="checkbox"
                      checked={filteredDecisions.length > 0 && filteredDecisions.every((d) => selectedArticles.includes(d.revision_id))}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      title="Select all manuscripts"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                    Manuscript
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                    Reviews Submitted
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                    Invited
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                    Expiry
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayDecisions.map((decision) => {
                  const isNew = decision.decision_viewed === 0;
                  return (
                    <tr key={decision.revision_id} className={`hover:bg-gray-50 transition-colors ${selectedArticles.includes(decision.revision_id) ? 'bg-purple-50' : ''}`}>
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedArticles.includes(decision.revision_id)}
                          onChange={() => toggleSelectArticle(decision.revision_id)}
                          className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          title="Select manuscript"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-3">
                          {isNew && (
                            <span className="mt-1 inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-700 animate-pulse">
                              NEW
                            </span>
                          )}
                          <div className="max-w-md">
                            <p className="text-sm cursor-pointer font-medium text-gray-900 truncate" onClick={() => handleReview(decision)}>{decision.title}</p>
                            <p className="text-xs text-[--asfi-purple] mt-1">{decision.revision_id}</p>
                            <p className="text-xs text-gray-500 mt-1">{decision.article_type}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{decision.reviews_count}</span>
                        <span className="text-sm text-gray-500"> / {decision.expected_reviews || decision.reviews_count}</span>
                        <p className="text-xs text-gray-400 mt-1">submitted reviews</p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(decision.invitation_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(decision.invitation_expiry_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusClass(decision.invitation_status || decision.status)}`}>
                          {formatStatus(decision.invitation_status || decision.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="flex items-center gap-1 mr-1">
                            <button
                              onClick={() => handleExportReviews(decision, 'pdf')}
                              disabled={!!exporting[decision.revision_id]}
                              title="Export all reviewer reports as PDF"
                              className="inline-flex items-center gap-1 px-2.5 py-2 bg-red-50 text-red-600 text-xs font-medium rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {exporting[decision.revision_id] === 'pdf' ? (
                                <Loader className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <FileText className="w-3.5 h-3.5" />
                              )}
                              <span className="hidden xl:inline">PDF</span>
                            </button>
                            <button
                              onClick={() => handleExportReviews(decision, 'docx')}
                              disabled={!!exporting[decision.revision_id]}
                              title="Export all reviewer reports as Word document"
                              className="inline-flex items-center gap-1 px-2.5 py-2 bg-blue-50 text-blue-600 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {exporting[decision.revision_id] === 'docx' ? (
                                <Loader className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Download className="w-3.5 h-3.5" />
                              )}
                              <span className="hidden xl:inline">Word</span>
                            </button>
                          </div>
                          <button
                            onClick={() => handleReview(decision)}
                            className="inline-flex items-center px-4 py-2 bg-[--asfi-blue] text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            Review & Decide
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages} ({totalItems} total)
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-8 h-8 rounded text-sm ${
                          currentPage === pageNum
                            ? 'bg-purple-600 text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PendingDecisions;
